/**
 * yoga-layout (official wasm build) adapter behind {@link ILayoutEngine}. The only file that
 * may import yoga. Load is async (wasm instantiation) — the host awaits `createYogaLayoutEngine()`
 * once (player: subsystem init; editor: UISubsystem/preview init).
 *
 * Defaults follow yoga/RN convention (every node is flex, `flex-direction: column`,
 * `align-items: stretch`) — the natural fit for screen UI; documented in the design doc.
 */
import {
  loadYoga,
  Align,
  Direction,
  Display,
  Edge,
  FlexDirection,
  Gutter,
  Justify,
  MeasureMode,
  Overflow,
  PositionType,
  Wrap,
} from 'yoga-layout/load';
import type { Dim } from '../types';
import {
  UIMeasureMode,
  type ILayoutEngine,
  type ILayoutNode,
  type LayoutInput,
  type UIMeasureFunc,
} from './ILayoutEngine';

type Yoga = Awaited<ReturnType<typeof loadYoga>>;
type YogaNodeHandle = ReturnType<Yoga['Node']['create']>;

const FLEX_DIRECTION = {
  row: FlexDirection.Row,
  'row-reverse': FlexDirection.RowReverse,
  column: FlexDirection.Column,
  'column-reverse': FlexDirection.ColumnReverse,
} as const;

const WRAP = {
  nowrap: Wrap.NoWrap,
  wrap: Wrap.Wrap,
  'wrap-reverse': Wrap.WrapReverse,
} as const;

const JUSTIFY = {
  'flex-start': Justify.FlexStart,
  center: Justify.Center,
  'flex-end': Justify.FlexEnd,
  'space-between': Justify.SpaceBetween,
  'space-around': Justify.SpaceAround,
  'space-evenly': Justify.SpaceEvenly,
} as const;

const ALIGN = {
  auto: Align.Auto,
  'flex-start': Align.FlexStart,
  center: Align.Center,
  'flex-end': Align.FlexEnd,
  stretch: Align.Stretch,
  baseline: Align.Baseline,
} as const;

const MEASURE_MODE: Record<number, UIMeasureMode> = {
  [MeasureMode.Undefined]: UIMeasureMode.Undefined,
  [MeasureMode.Exactly]: UIMeasureMode.Exactly,
  [MeasureMode.AtMost]: UIMeasureMode.AtMost,
};

/** Dim → the value yoga setters accept (number | '…%' | 'auto' | undefined = reset). */
function dim(v: Dim | undefined): number | `${number}%` | 'auto' | undefined {
  if (v === undefined) return undefined;
  if (typeof v === 'number') return v;
  if (v === 'auto') return 'auto';
  if ('calc' in v) return undefined; // unresolved unit combo — UIDocument bakes these to px first
  return `${v.pct}%`;
}

/** Dim → yoga value where 'auto' is not a legal input (min/max/inset). */
function dimNoAuto(v: Dim | undefined): number | `${number}%` | undefined {
  const d = dim(v);
  return d === 'auto' ? undefined : d;
}

class YogaLayoutNode implements ILayoutNode {
  constructor(readonly node: YogaNodeHandle) {}

  insertChild(child: ILayoutNode, index: number): void {
    this.node.insertChild((child as YogaLayoutNode).node, index);
  }

  removeChild(child: ILayoutNode): void {
    this.node.removeChild((child as YogaLayoutNode).node);
  }

  applyStyle(s: LayoutInput): void {
    const n = this.node;
    n.setDisplay(s.display === 'none' ? Display.None : Display.Flex);
    n.setFlexDirection(FLEX_DIRECTION[s.flexDirection ?? 'column']);
    n.setFlexWrap(WRAP[s.flexWrap ?? 'nowrap']);
    n.setFlexGrow(s.flexGrow ?? 0);
    n.setFlexShrink(s.flexShrink ?? 0);
    n.setFlexBasis(dim(s.flexBasis));
    n.setJustifyContent(JUSTIFY[s.justifyContent ?? 'flex-start']);
    n.setAlignItems(ALIGN[s.alignItems ?? 'stretch']);
    n.setAlignSelf(ALIGN[s.alignSelf ?? 'auto']);
    n.setAlignContent(ALIGN[s.alignContent ?? 'flex-start']);
    n.setGap(Gutter.Row, s.rowGap ?? 0);
    n.setGap(Gutter.Column, s.columnGap ?? 0);

    n.setWidth(dim(s.width));
    n.setHeight(dim(s.height));
    n.setMinWidth(dimNoAuto(s.minWidth));
    n.setMinHeight(dimNoAuto(s.minHeight));
    n.setMaxWidth(dimNoAuto(s.maxWidth));
    n.setMaxHeight(dimNoAuto(s.maxHeight));

    n.setMargin(Edge.Top, dim(s.marginTop));
    n.setMargin(Edge.Right, dim(s.marginRight));
    n.setMargin(Edge.Bottom, dim(s.marginBottom));
    n.setMargin(Edge.Left, dim(s.marginLeft));
    n.setPadding(Edge.Top, dimNoAuto(s.paddingTop));
    n.setPadding(Edge.Right, dimNoAuto(s.paddingRight));
    n.setPadding(Edge.Bottom, dimNoAuto(s.paddingBottom));
    n.setPadding(Edge.Left, dimNoAuto(s.paddingLeft));

    // CSS box model: border takes LAYOUT space (children sit inside the border ring and can
    // never overlap the painted border — see ui-rect's inward SDF border).
    n.setBorder(Edge.All, s.borderWidth ?? 0);

    n.setPositionType(s.position === 'absolute' ? PositionType.Absolute : PositionType.Relative);
    n.setPosition(Edge.Top, dimNoAuto(s.top));
    n.setPosition(Edge.Right, dimNoAuto(s.right));
    n.setPosition(Edge.Bottom, dimNoAuto(s.bottom));
    n.setPosition(Edge.Left, dimNoAuto(s.left));

    n.setAspectRatio(s.aspectRatio);
    n.setOverflow(
      s.overflow === 'hidden' ? Overflow.Hidden : s.overflow === 'scroll' ? Overflow.Scroll : Overflow.Visible,
    );
  }

  setMeasureFunc(fn: UIMeasureFunc | null): void {
    if (!fn) {
      this.node.setMeasureFunc(null);
      return;
    }
    this.node.setMeasureFunc((w, wMode, h, hMode) =>
      fn(w, MEASURE_MODE[wMode] ?? UIMeasureMode.Undefined, h, MEASURE_MODE[hMode] ?? UIMeasureMode.Undefined),
    );
  }

  markDirty(): void {
    this.node.markDirty();
  }

  calculateLayout(availableWidth: number, availableHeight: number): void {
    this.node.calculateLayout(availableWidth, availableHeight, Direction.LTR);
  }

  hasNewLayout(): boolean {
    return this.node.hasNewLayout();
  }

  markLayoutSeen(): void {
    this.node.markLayoutSeen();
  }

  get left(): number {
    return this.node.getComputedLeft();
  }
  get top(): number {
    return this.node.getComputedTop();
  }
  get width(): number {
    return this.node.getComputedWidth();
  }
  get height(): number {
    return this.node.getComputedHeight();
  }

  free(): void {
    this.node.free();
  }
}

class YogaLayoutEngine implements ILayoutEngine {
  constructor(private readonly yoga: Yoga) {}

  createNode(): ILayoutNode {
    return new YogaLayoutNode(this.yoga.Node.create());
  }
}

export interface YogaEngineOptions {
  /**
   * Explicit wasm bytes (player: shipped as `wasm/yoga.wasm` next to the rapier wasm — the
   * build-time yoga plugin STRIPS the embedded base64 from player bundles, so the file is the
   * only source there). Editor/tests omit it and use the package's embedded binary.
   */
  wasmBinary?: ArrayBuffer | Uint8Array;
}

let enginePromise: Promise<ILayoutEngine> | null = null;

/** Load (once) the yoga wasm module and return the engine. Safe to call repeatedly —
 *  the first caller's options win (one wasm instance per runtime). */
export function createYogaLayoutEngine(options?: YogaEngineOptions): Promise<ILayoutEngine> {
  enginePromise ??= (async () => {
    const g = globalThis as { __YOGA_WASM_BINARY__?: ArrayBuffer | Uint8Array };
    // Transport into yoga's emscripten factory: player bundles are transformed (see the
    // yoga-wasm esbuild plugin) to read this global as Module.wasmBinary; untransformed
    // builds (editor/vite, bun tests) ignore it and use the embedded binary.
    if (options?.wasmBinary) g.__YOGA_WASM_BINARY__ = options.wasmBinary;
    try {
      const yoga = await loadYoga();
      return new YogaLayoutEngine(yoga);
    } finally {
      delete g.__YOGA_WASM_BINARY__;
    }
  })();
  return enginePromise;
}
